import { useEffect, useState } from "react";
import type { PinCandidate } from "@keydials/api-client";
import { Avatar, avatarColour, initialsOf } from "@keydials/dial-workspace";
import { api, useAuth } from "../auth/AuthContext";
import { Icon } from "./Icon";

/** Enough digits for the shortest accepted PIN before "Unlock" lights up; the server enforces the real bounds. */
const MIN_PIN_LENGTH = 4;
const MAX_PIN_LENGTH = 6;

/** Big targets throughout: the person using this is on a round, standing up, quite possibly gloved. */
const KEY_SIZE = 76;

// Seeded on the user id, not the email, because the candidate payload deliberately carries no email. That
// means a carer's avatar colour here won't match their avatar in the dial's member list (which seeds on
// email) — a small cost, and the right way round: keeping addresses off a corridor-facing screen matters
// more than colour continuity between two screens you never see side by side.
function personFor(candidate: PinCandidate) {
  return {
    name: candidate.displayName,
    initials: initialsOf(candidate.displayName),
    colour: avatarColour(candidate.userId),
  };
}

/**
 * Waking a locked shared device (KEY-268): pick your name, enter your PIN, and land back in the dial that
 * was already open — and only that dial.
 *
 * The candidate list comes from the lock ticket, so it is scoped to this dial's members who have set a PIN,
 * and carries names only. Every rejection reads the same, because the server deliberately doesn't say
 * whether the PIN was wrong, missing, or locked out.
 */
export function PinLogin({ onCancel }: { onCancel: () => void }) {
  const { lock, unlock, logout } = useAuth();
  const [candidates, setCandidates] = useState<PinCandidate[] | null>(null);
  const [selected, setSelected] = useState<PinCandidate | null>(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lock) return;
    api
      .pinCandidates(lock.ticket, lock.instanceId)
      .then(setCandidates)
      .catch(() => setCandidates([]));
  }, [lock]);

  if (!lock) {
    return null;
  }

  async function submit(candidate: PinCandidate, value: string) {
    setBusy(true);
    setError(null);
    const ok = await unlock(candidate.userId, value);
    if (!ok) {
      // One message for every rejection — matching the server, which gives one response for a wrong PIN, a
      // name with no PIN, and an account in its lockout window alike.
      setError("That PIN wasn't recognised. Try again, or sign in with your email.");
      setPin("");
      setBusy(false);
    }
    // On success the provider swaps the token and the app re-renders into the dial; nothing to do here.
  }

  function press(digit: string) {
    if (busy || !selected) return;
    setError(null);
    setPin((current) => {
      const next = (current + digit).slice(0, MAX_PIN_LENGTH);
      return next;
    });
  }

  const canSubmit = !!selected && pin.length >= MIN_PIN_LENGTH && !busy;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 22,
        padding: 24,
        overflowY: "auto",
      }}
    >
      {!selected ? (
        <>
          <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text)" }}>Who's signing in?</div>
          {candidates === null ? (
            <div style={{ color: "var(--text-muted)", fontSize: 14 }}>Loading…</div>
          ) : candidates.length === 0 ? (
            <div style={{ maxWidth: 420, textAlign: "center", color: "var(--text-muted)", fontSize: 14.5, lineHeight: 1.5 }}>
              Nobody on this dial has set a PIN yet. Sign in with your email address, then set one from the
              account menu so you can unlock this screen next time.
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 18, justifyContent: "center", maxWidth: 760 }}>
              {candidates.map((candidate) => (
                <button
                  key={candidate.userId}
                  type="button"
                  onClick={() => {
                    setSelected(candidate);
                    setPin("");
                    setError(null);
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 10,
                    width: 132,
                    padding: "18px 10px",
                    borderRadius: "var(--radius)",
                    border: "var(--border-w) solid var(--border)",
                    background: "var(--surface)",
                    cursor: "pointer",
                    font: "inherit",
                  }}
                >
                  <Avatar person={personFor(candidate)} size={64} ring={false} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", textAlign: "center", wordBreak: "break-word" }}>
                    {candidate.displayName}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <Avatar person={personFor(selected)} size={64} ring={false} />
            <div style={{ fontSize: 19, fontWeight: 700, color: "var(--text)" }}>{selected.displayName}</div>
            <div style={{ fontSize: 14, color: "var(--text-muted)" }}>Enter your PIN</div>
          </div>

          {/* Dots rather than the digits: this screen faces a corridor. */}
          <div style={{ display: "flex", gap: 12, minHeight: 20 }} aria-label={`${pin.length} of ${MAX_PIN_LENGTH} digits entered`}>
            {Array.from({ length: MAX_PIN_LENGTH }, (_, i) => (
              <span
                key={i}
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 99,
                  background: i < pin.length ? "var(--accent)" : "transparent",
                  border: `2px solid ${i < pin.length ? "var(--accent)" : "var(--border)"}`,
                }}
              />
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: `repeat(3, ${KEY_SIZE}px)`, gap: 12 }}>
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((digit) => (
              <PinKey key={digit} label={digit} onClick={() => press(digit)} disabled={busy} />
            ))}
            <PinKey
              label={<Icon name="back" size={22} />}
              ariaLabel="Delete"
              onClick={() => setPin((c) => c.slice(0, -1))}
              disabled={busy || pin.length === 0}
            />
            <PinKey label="0" onClick={() => press("0")} disabled={busy} />
            <PinKey
              label={<Icon name="check" size={24} />}
              ariaLabel="Unlock"
              primary
              onClick={() => canSubmit && void submit(selected, pin)}
              disabled={!canSubmit}
            />
          </div>

          {error && (
            <div role="alert" style={{ color: "var(--st-overdue)", fontSize: 14, textAlign: "center", maxWidth: 340 }}>
              {error}
            </div>
          )}
        </>
      )}

      <div style={{ display: "flex", gap: 18, marginTop: 8 }}>
        <button
          type="button"
          onClick={() => (selected ? (setSelected(null), setPin(""), setError(null)) : onCancel())}
          style={linkStyle}
        >
          {selected ? "Choose a different name" : "Back"}
        </button>
        <button type="button" onClick={() => void logout()} style={linkStyle}>
          Sign in with email instead
        </button>
      </div>
    </div>
  );
}

const linkStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: "8px 4px",
  font: "inherit",
  fontSize: 13.5,
  color: "var(--text-faint)",
  textDecoration: "underline",
  cursor: "pointer",
};

function PinKey({
  label,
  ariaLabel,
  onClick,
  disabled,
  primary,
}: {
  label: React.ReactNode;
  ariaLabel?: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{
        width: KEY_SIZE,
        height: KEY_SIZE,
        borderRadius: "var(--radius)",
        border: "var(--border-w) solid var(--border)",
        background: primary ? "var(--accent)" : "var(--surface)",
        color: primary ? "#fff" : "var(--text)",
        font: "inherit",
        fontSize: 26,
        fontWeight: 600,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.45 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {label}
    </button>
  );
}
